import { TRANSITION_BORDER } from '@/components/ui/motion.ts';
import { CONTROL_SURFACE_SHADOW } from '@/components/ui/shadows.ts';

const BASE_INPUT_STYLES: Record<string, unknown> = {
	h: 12,
	px: 4,
	border: '1px solid',
	borderColor: 'border.default',
	bg: 'surface.default',
	transition: 'border-color 0.15s ease',
	_placeholder: { color: 'fg.subtle' },
	_hover: {
		'&:not(:disabled):not([readonly]):not([aria-disabled=true]):not(:focus):not(:focus-within)': {
			borderColor: 'border.strong',
		},
	},
	// Highlight the active/selected field by recolouring its own (rounded) border.
	// The default focus ring renders as an outline, which ignores border-radius on
	// iOS Safari < 16.4 and shows square corners; _focusVisible strips that outline.
	focusRing: 'none',
	_focus: { borderColor: 'focus.ring' },
	_focusVisible: { borderColor: 'focus.ring', outline: 'none', boxShadow: 'none' },
};

/**
 * Shared styling for Select triggers that should read as a field: pill border with a
 * darken-on-hover edge, matching the rounded inputs above.
 */
export const SELECT_TRIGGER_PROPS = {
	borderRadius: 'full',
	bg: 'surface.default',
	border: '1px solid',
	borderColor: 'border.default',
	color: 'fg',
	cursor: 'pointer',
	boxShadow: CONTROL_SURFACE_SHADOW,
	transition: TRANSITION_BORDER,
	_hover: { borderColor: 'border.strong' },
} as const;

export const READONLY_INPUT_PROPS: Record<string, unknown> = {
	readOnly: true,
	border: '1px solid',
	borderColor: 'surface.default',
	color: 'fg.muted',
	cursor: 'default',
	focusRingWidth: '0px',
	focusRingColor: 'transparent',
	_focus: { outline: 'none', boxShadow: 'none' },
	_focusVisible: { outline: 'none', boxShadow: 'none' },
};

export const getInputStyles = (props: Record<string, unknown> = {}) => {
	const rows = typeof props.rows === 'number' ? props.rows : Number(props.rows);
	const isMultiline = Number.isFinite(rows) && rows > 1;

	return {
		...BASE_INPUT_STYLES,
		borderRadius: isMultiline ? '2xl' : 'full',
		h: isMultiline ? undefined : 12,
		minH: isMultiline ? 24 : undefined,
	};
};
